'use client';

import { useParams, useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useSession } from '@/hooks/useSessionHook';
import { useBotChat } from '@/hooks/use-bot-chat';
import { BotChatHeader, BotChatWindow, BotChatInput } from '@/components/bots';

export default function BotChatPage() {
  const { botId } = useParams<{ botId: string }>();
  const navigate = useNavigate();
  const { session } = useSession();
  const userId = session?.user?.id;

  const {
    bot,
    messages,
    isLoading,
    isSending,
    isStreaming,
    streamingContent,
    error,
    sendMessage,
    remainingMessages,
  } = useBotChat({
    botId: botId || '',
    userId: userId || '',
    enabled: !!botId && !!userId,
  });

  const handleBack = () => {
    navigate(-1);
  };

  const handleGoToBots = () => {
    navigate('/bots');
  };

  // Loading state
  if (isLoading || !userId) {
    return (
      <div className="jr-app-shell">
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-[var(--jr-accent)]" />
        </div>
      </div>
    );
  }

  // Bot not found
  if (!bot) {
    return (
      <div className="jr-app-shell">
        <header className="jr-subheader">
          <Button variant="ghost" onClick={handleBack} className="rounded-full text-[var(--jr-text)] hover:bg-[color:rgba(255,255,255,0.06)]">
            Back
          </Button>
          <h2 className="jr-display ml-2 text-2xl font-semibold">Bot Chat</h2>
        </header>
        <div className="flex-1 flex flex-col items-center justify-center p-6">
          <p className="mb-4 text-sm text-[color:var(--jr-danger)]">
            {error || 'Bot not found'}
          </p>
          <Button onClick={handleGoToBots} className="jr-action-primary h-auto min-h-11">Browse Bots</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="jr-app-shell">
      <BotChatHeader
        bot={bot}
        onBack={handleBack}
        remainingMessages={remainingMessages}
      />

      {error && (
        <div
          className="mx-4 mt-3 rounded-2xl border border-[color:rgba(183,90,86,0.22)] bg-[color:rgba(110,43,47,0.18)] px-4 py-3 text-sm text-[color:#efc2bd]"
          data-testid="bot-chat-error"
        >
          {error}
        </div>
      )}

      <BotChatWindow
        messages={messages}
        bot={bot}
        isLoading={isLoading}
        isSending={isSending}
        isStreaming={isStreaming}
        streamingContent={streamingContent}
        className="flex-1 min-h-0"
      />

      <BotChatInput
        onSendMessage={sendMessage}
        disabled={!bot}
        isSending={isSending}
        placeholder={`Message ${bot.name}...`}
      />
    </div>
  );
}
